import React, { useRef } from "react";
import { Link } from "react-router";
import Footer from "../../components/footer/footer";
import "./privacyPolicy.css";

const PrivacyPolicy = () => {
  const topicOneRef = useRef(null);
  const topicTwoRef = useRef(null);
  const topicThreeRef = useRef(null);
  const topicFourRef = useRef(null);
  const topicFiveRef = useRef(null);
  const topicSixRef = useRef(null);
  const topicSevenRef = useRef(null);
  const topicEightRef = useRef(null);
  const topicNineRef = useRef(null);
  const topicTenRef = useRef(null);
  const topicElevenRef = useRef(null);
  const topicTwelveRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="privacy-policy">
      <div className="pp-hero">
        <div className="pp-hero-inner">
          <Link className="pp-back" to="/">← Back to home</Link>
          <span className="pp-eyebrow">Legal</span>
          <h1>Privacy Policy</h1>
          <span className="pp-updated">Last updated: March 11, 2025</span>
        </div>
      </div>

      <div className="pp-doc">
      <p>
        This privacy notice for DevTechInvento (
        <b>“Company”,“we,” “us,” or “our”</b>), describes how and why we might
        collect, store, store, use, and/or share (<b>“process”</b>) your
        information when you use our services (<b>“Services”</b>), such as when
        you:{" "}
      </p>

      <ul className="list">
        <li>
          Download and use our mobile application (Food-Is), or any other
          application of ours that links to this privacy notice
        </li>
        <li>
          Engage with us in other related ways, including any sales, marketing,
          or events
        </li>
      </ul>

      <p>
        <b>Questions or concerns?</b> Reading this privacy notice will help you
        understand your privacy rights and choices. If you do not agree with our
        policies and practices, please do not use our Services. If you still
        have any questions or concerns, please contact us at
        <a href="mailto:techinvento@icloud.com">
          &nbsp;techinvento@icloud.com
        </a>
        ,
        <a href="mailto:devtechinventoinfo@gmail.com">
          &nbsp;devtechinventoinfo@gmail.com
        </a>
        .
      </p>

      <h3>SUMMARY OF KEY POINTS</h3>

      <p>
        <b>
          This summary provides key points from our privacy notice, but you can
          find out more details about any of these topics by clicking the link
          following each key point or by using our table of contents below to
          find the section you are looking for.
        </b>
      </p>
      <p>
        <b>What personal information do we process?</b> When you visit, use, or
        navigate our Services, we may process personal information depending on
        how you interact with DevTechInvento and the Services, the choices you
        make, and the products and features you use. Learn more about the
        personal information you disclose to us.
      </p>
      <p>
        <b>Do we process any sensitive personal information?</b> We do not
        process sensitive personal information.
      </p>
      <p>
        <b>Do we receive any information from third parties?</b> We do not
        receive any information from third parties.
      </p>
      <p>
        <b>How do we process your information?</b> We process your information
        to provide, improve, and administer our Services, communicate with you,
        for security and fraud prevention, and to comply with law. We may also
        process your information for other purposes with your consent. We
        process your information only when we have a valid legal reason to do
        so. Learn more about when and why we process your information.
      </p>
      <p>
        <b>
          In what situations and with which parties do we share personal
          information?
        </b>
        We may share information in specific situations and with specific third
        parties. Learn more about when and with whom we share your personal
        information.
      </p>
      <p>
        <b>How do we keep your information safe?</b> We have organizational and
        technical processes and procedures in place to protect your personal
        information. However, no electronic transmission over the internet or
        information storage technology can be guaranteed to be 100% secure, so
        we cannot promise or guarantee that hackers, cybercriminals, or other
        unauthorized third parties will not be able to defeat our security and
        improperly collect, access, steal, or modify your information. Learn
        more about how we keep your information safe.
      </p>
      <p>
        <b>What are your rights?</b> Depending on where you are located
        geographically, the applicable privacy law may mean you have certain
        rights regarding your personal information. Learn more about your
        privacy rights.
      </p>
      <p>
        <b>How do you exercise your rights?</b> The easiest way to exercise your
        rights is by filling out our data subject request form available here:
        <a href="mailto:techinvento@icloud.com">
          &nbsp;techinvento@icloud.com
        </a>
        ,
        <a href="mailto:devtechinventoinfo@gmail.com">
          &nbsp;devtechinventoinfo@gmail.com
        </a>
        . We will consider and act upon any request in accordance with
        applicable data protection laws.
      </p>
      <p>
        <b>
          Want to learn more about what DevTechInvento does with any information
          we collect?
        </b>
        Review the privacy notice in full.
      </p>

      <h3>TABLE OF CONTENTS</h3>
      <ul className="list toc-list">
        <li onClick={() => scrollToSection(topicOneRef)}>
          1. WHAT INFORMATION DO WE COLLECT?
        </li>
        <li onClick={() => scrollToSection(topicTwoRef)}>
          2. HOW DO WE PROCESS YOUR INFORMATION?
        </li>
        <li onClick={() => scrollToSection(topicThreeRef)}>
          3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
        </li>
        <li onClick={() => scrollToSection(topicFourRef)}>
          4. HOW LONG DO WE KEEP YOUR INFORMATION?
        </li>
        <li onClick={() => scrollToSection(topicFiveRef)}>
          5. HOW DO WE KEEP YOUR INFORMATION SAFE?
        </li>
        <li onClick={() => scrollToSection(topicSixRef)}>
          6. DO WE COLLECT INFORMATION FROM MINORS?
        </li>
        <li onClick={() => scrollToSection(topicSevenRef)}>
          7. WHAT ARE YOUR PRIVACY RIGHTS?
        </li>
        <li onClick={() => scrollToSection(topicEightRef)}>
          8. CONTROLS FOR DO-NOT-TRACK FEATURES
        </li>
        <li onClick={() => scrollToSection(topicNineRef)}>
          9. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
        </li>
        <li onClick={() => scrollToSection(topicTenRef)}>
          10. DO WE MAKE UPDATES TO THIS NOTICE?
        </li>
        <li onClick={() => scrollToSection(topicElevenRef)}>
          11. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
        </li>
        <li onClick={() => scrollToSection(topicTwelveRef)}>
          12. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
          YOU?
        </li>
      </ul>

      <h3 ref={topicOneRef}>1. WHAT INFORMATION DO WE COLLECT?</h3>
      <h4>Personal information you disclose to us</h4>
      <p>
        <i>
          <b>In Short:</b> We collect personal information that you provide to
          us.
        </i>
      </p>
      <p>
        We collect personal information that you voluntarily provide to us when
        you express an interest in obtaining information about us or our
        products and Services when you participate in activities on the
        Services, or otherwise when you contact us.
      </p>
      <p>
        <b>Sensitive Information.</b> We do not process sensitive information.
      </p>

      <p>
        <b>Application Data.</b> If you use our application(s), we also may
        collect the following information if you choose to provide us with
        access or permission:
      </p>
      <ul className="list">
        <li>
          <i>Mobile Device Access.</i> We may request access or permission to
          certain features from your mobile device, including your mobile
          device’s camera. If you wish to change our access or permissions, you
          may do so in your device’s settings.
        </li>
        <li>
          <i>Mobile Device Data.</i> We automatically collect device information
          (such as your mobile device ID, model, and manufacturer), operating
          system, version information and system configuration information,
          device and application identification numbers, browser type and
          version, hardware model Internet service provider and/or mobile
          carrier and Internet Protocol (IP) address (or proxy server). If you
          are using our application(s), we may also collect information about
          the phone network associated with your mobile device, your mobile
          device’s operating system or platform, the type of mobile device you
          use, your mobile device’s unique device ID, and information about the
          features of our application(s) you accessed.
        </li>
        <li>
          <i>Push Notifications.</i> We may request to send you push
          notifications regarding your account or certain features of the
          application(s). If you wish to opt out from receiving these types of
          communications, you may turn them off in your device’s settings.
        </li>
      </ul>
      <p>
        This information is primarily needed to maintain the security and
        operation of our application(s), for troubleshooting, and for our
        internal analytics and reporting purposes.
      </p>
      <p>
        All personal information that you provide to us must be true, complete,
        and accurate, and you must notify us of any changes to such personal
        information.
      </p>

      <h4>Information automatically collected</h4>
      <p>
        <i>
          <b>In Short:</b> Some information — such as your Internet Protocol
          (IP) address and/or browser and device characteristics — is collected
          automatically when you visit our Services.
        </i>
      </p>
      <p>
        We automatically collect certain information when you visit, use, or
        navigate our Services. This information does not reveal your specific
        identity (like your name or contact information) but may include device
        and usage information, such as your IP address, browser and device
        characteristics, operating system, language preferences, referring URLs,
        device name, country, location, information about how and when you use
        our Services, and other technical information. This information is
        primarily needed to maintain the security and operation of our Services,
        and for our internal analytics and reporting purposes.
      </p>
      <p>The information we collect includes:</p>
      <ul className="list">
        <li>
          <i>Log and Usage Data.</i> Log and usage data is service-related,
          diagnostic, usage, and performance information our servers
          automatically collect when you access or use our Services and which we
          record in log files. Depending on how you interact with us, this log
          data may include your IP address, device information, browser type,
          and settings and information about your activity in the Services (such
          as the date/time stamps associated with your usage, pages and files
          viewed, searches, and other actions you take such as which features
          you use), device event information (such as system activity, error
          reports (sometimes called 'crash dumps'), and hardware settings).
        </li>
        <li>
          <i>Device Data.</i> We collect device data such as information about
          your computer, phone, tablet, or other device you use to access the
          Services. Depending on the device used, this device data may include
          information such as your IP address (or proxy server), device and
          application identification numbers, location, browser type, hardware
          model, Internet service provider and/or mobile carrier, operating
          system and system configuration information.
        </li>
        <li>
          Location Data. We collect location data such as information about your
          device’s location, which can be either precise or imprecise. How much
          information we collect depends on the type and settings of the device
          you use to access the Services. For example, we may use GPS and other
          technologies to collect geolocation data that tells us your current
          location (based on your IP address). You can opt out of allowing us to
          collect this information either by refusing access to the information
          or by disabling your Location setting on your device. However, if you
          choose to opt out, you may not be able to use certain aspects of the
          Services.
        </li>
      </ul>
      <h3 ref={topicTwoRef}>2. HOW DO WE PROCESS YOUR INFORMATION?</h3>
      <p>
        <i>
          <b>In Short:</b> We process your information to provide, improve, and
          administer our Services, communicate with you, for security and fraud
          prevention, and to comply with law. We may also process your
          information for other purposes with your consent.
        </i>
      </p>
      <p>
        <b>
          We process your personal information for a variety of reasons,
          depending on how you interact with our Services, including:
        </b>
      </p>
      <ul className="list">
        <li>
          <b>
            To evaluate and improve our Services, products, marketing, and your
            experience.
          </b>{" "}
          We may process your information when we believe it is necessary to
          identify usage trends, determine the effectiveness of our promotional
          campaigns, and evaluate and improve our Services, products, marketing,
          and your experience.
        </li>
      </ul>

      <h3 ref={topicThreeRef}>
        3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
      </h3>
      <p>
        <i>
          <b>In Short:</b> We may share information in specific situations
          described in this section and/or with the following third parties.
        </i>
      </p>
      <p>
        We may need to share your personal information in the following
        situations:
      </p>
      <p>
        <b>Business Transfers.</b> We may share or transfer your information in
        connection with, or during negotiations of, any merger, sale of company
        assets, financing, or acquisition of all or a portion of our business to
        another company.
      </p>
      <h3 ref={topicFourRef}>4. HOW LONG DO WE KEEP YOUR INFORMATION?</h3>
      <p>
        <i>
          <b>In Short:</b> We keep your information for as long as necessary to
          fulfill the purposes outlined in this privacy notice unless otherwise
          required by law.
        </i>
      </p>
      <p>
        We will only keep your personal information for as long as it is
        necessary for the purposes set out in the privacy notice unless a longer
        retention period is required or permitted by law (such as tax,
        accounting, or other legal requirements). No purpose in this notice will
        require us to keep your personal information for longer than 6 months.
      </p>
      <p>
        When we have no ongoing legitimate business need to process your
        personal information, we will either delete or anonymize such
        information, or, if this is not possible (for example, because your
        personal information has been stored in backup archives), then we will
        securely store your personal information and isolate it from any further
        processing until deletion is possible.
      </p>
      <h3 ref={topicFiveRef}>5. HOW DO WE KEEP YOUR INFORMATION SAFE?</h3>
      <p>
        <i>
          <b>In Short:</b> We aim to protect your personal information through a
          system of organizational and technical security measures.
        </i>
      </p>
      <p>
        We have implemented appropriate technical and organizational security
        measures designed to protect the security of any personal information we
        process. However, despite our safeguards and efforts to secure your
        information, no electronic transmission over the Internet or information
        storage technology can be guaranteed to be 100% secure, so we cannot
        promise or guarantee that hackers, cybercriminals, or other unauthorized
        third parties will not be able to defeat our security and improperly
        collect, access, steal, or modify your information. Although we will do
        our best to protect your personal information, transmission of personal
        information to and from our Services is at your own risk. You should
        only access the Services within a secure environment.
      </p>
      <h3 ref={topicSixRef}>6. DO WE COLLECT INFORMATION FROM MINORS?</h3>
      <p>
        <i>
          <b>In Short:</b> We do not knowingly collect data from or market to
          children under 18 years of age.
        </i>
      </p>
      <p>
        We do not knowingly solicit data from or market to children under 18
        years of age. By using the Services, you represent that you are at least
        18 or that you are the parent or guardian of such a minor and consent to
        such minor dependent’s use of the Services. If we learn that personal
        information from users less than 18 years of age has been collected, we
        will deactivate the account and take reasonable measures to promptly
        delete such data from our records. If you become aware of any data we
        may have collected from children under age 18, please contact us at
        <a href="mailto:techinvento@icloud.com">
          &nbsp;techinvento@icloud.com
        </a>
        ,
        <a href="mailto:devtechinventoinfo@gmail.com">
          &nbsp;devtechinventoinfo@gmail.com
        </a>
        .
      </p>
      <h3 ref={topicSevenRef}>7. WHAT ARE YOUR PRIVACY RIGHTS?</h3>
      <p>
        <i>
          <b>In Short:</b> You may review, change, or terminate your account at
          any time.
        </i>
      </p>
      <p>
        If you are located in the EEA or UK and you believe we are unlawfully
        processing your personal information, you may also have the right to
        complain to your
        <a href="https://ec.europa.eu/newsroom/article29/items/612080">
          &nbsp;Member State data protection authority
        </a>
        &nbsp;or&nbsp;
        <a href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/">
          &nbsp;UK data protection authority&nbsp;
        </a>
        .
      </p>
      <p>
        If you are located in Switzerland, you may contact the
        <a href="https://www.edoeb.admin.ch/edoeb/en/home.html">
          &nbsp;Federal Data Protection and Information Commissioner
        </a>
        .
      </p>
      <p>
        <b>
          <u>Withdrawing your consent:</u>
        </b>
        If we are relying on your consent to process your personal information,
        which may be express and/or implied consent depending on the applicable
        law, you have the right to withdraw your consent at any time. You can
        withdraw your consent at any time by contacting us by using the contact
        details provided in the section “HOW CAN YOU CONTACT US ABOUT THIS
        NOTICE?” below.
      </p>
      <p>
        However, please note that this will not affect the lawfulness of the
        processing before its withdrawal nor, when applicable law allows, will
        it affect the processing of your personal information conducted in
        reliance on lawful processing grounds other than consent.
      </p>
      <p>
        If you have questions or comments about your privacy rights, you may
        email us at
        <a href="mailto:techinvento@icloud.com">
          &nbsp;techinvento@icloud.com
        </a>
        ,
        <a href="mailto:devtechinventoinfo@gmail.com">
          &nbsp;devtechinventoinfo@gmail.com
        </a>
        .
      </p>
      <h3 ref={topicEightRef}>8. CONTROLS FOR DO-NOT-TRACK FEATURES</h3>
      <p>
        Most web browsers and some mobile operating systems and mobile
        applications include a Do-Not-Track (“DNT”) feature or setting you can
        activate to signal your privacy preference not to have data about your
        online browsing activities monitored and collected. At this stage no
        uniform technology standard for recognizing and implementing DNT signals
        has been finalized. As such, we do not currently respond to DNT browser
        signals or any other mechanism that automatically communicates your
        choice not to be tracked online. If a standard for online tracking is
        adopted that we must follow in the future, we will inform you about that
        practice in a revised version of this privacy notice.
      </p>
      <h3 ref={topicNineRef}>
        9. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
      </h3>
      <p>
        <i>
          <b>In Short:</b> Yes, if you are a resident of California, you are
          granted specific rights regarding access to your personal information.
        </i>
      </p>
      <p>
        California Civil Code Section 1798.83, also known as the “Shine The
        Light” law, permits our users who are California residents to request
        and obtain from us, once a year and free of charge, information about
        categories of personal information (if any) we disclosed to third
        parties for direct marketing purposes and the names and addresses of all
        third parties with which we shared personal information in the
        immediately preceding calendar year. If you are a California resident
        and would like to make such a request, please submit your request in
        writing to us using the contact information provided below.
      </p>

      <p>
        If you are under 18 years of age, reside in California, and have a
        registered account with Services, you have the right to request the
        removal of unwanted data that you publicly post on the Services. To
        request removal of such data, please contact us using the contact
        information provided below and include the email address associated with
        your account and a statement that you reside in California. We will make
        sure the data is not publicly displayed on the Services, but please be
        aware that the data may not be completely or comprehensively removed
        from all our systems (e.g., backups, etc.).
      </p>
      <h4>CCPA Privacy Notice</h4>
      <p>The California Code of Regulations defines a “resident” as:</p>
      <ul className="list">
        <li>
          (1) every individual who is in the State of California for other than
          a temporary or transitory purpose and
        </li>
        <li>
          (2) every individual who is domiciled in the State of California who
          is outside the State of California for a temporary or transitory
          purpose
        </li>
      </ul>
      <p>All other individuals are defined as “non-residents.”</p>
      <p>
        If this definition of “resident” applies to you, we must adhere to
        certain rights and obligations regarding your personal information.
      </p>
      <h4>What categories of personal information do we collect?</h4>
      <p>
        We have collected the following categories of personal information in
        the past twelve (12) months:
      </p>
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Examples</th>
            <th>Collected</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>A. Identifiers</td>
            <td>
              Contact details, such as real name, alias, postal address, email
              address, and telephone number
            </td>
            <td>NO</td>
          </tr>
          <tr>
            <td>
              B. Personal information categories listed in the California
              Customer Records statute
            </td>
            <td>
              Name, contact information, education, employment, employment
              history, and financial information
            </td>
            <td>NO</td>
          </tr>
          <tr>
            <td>
              C. Protected classification characteristics under California or
              federal law
            </td>
            <td>Gender and date of birth</td>
            <td>NO</td>
          </tr>
          <tr>
            <td>D. Commercial information</td>
            <td>
              Transaction information, purchase history, financial details, and
              payment information
            </td>
            <td>NO</td>
          </tr>
          <tr>
            <td>E. Biometric information</td>
            <td>Fingerprints and voiceprints</td>
            <td>NO</td>
          </tr>
          <tr>
            <td>F. Internet or other similar network activity</td>
            <td>
              Browser history, search history, and information regarding a
              consumer’s interaction with an Internet website, application, or
              advertisement
            </td>
            <td>NO</td>
          </tr>
          <tr>
            <td>G. Geolocation data</td>
            <td>Device location</td>
            <td>NO</td>
          </tr>
          <tr>
            <td>
              H. Audio, electronic, visual, thermal, olfactory, or similar
              information
            </td>
            <td>
              Images and audio, video or call recordings created in connection
              with our business activities
            </td>
            <td>NO</td>
          </tr>
          <tr>
            <td>I. Professional or employment-related information</td>
            <td>
              Business contact details in order to provide you with our services
              at a business level or job title, work history, and professional
              qualifications if you apply for a job with us
            </td>
            <td>NO</td>
          </tr>
          <tr>
            <td>J. Education Information</td>
            <td>Student records and directory information</td>
            <td>NO</td>
          </tr>
          <tr>
            <td>K. Inferences drawn from other personal information</td>
            <td>
              Inferences drawn from any of the collected personal information
              listed above to create a profile or summary about, for example, an
              individual’s preferences and characteristics
            </td>
            <td>NO</td>
          </tr>
          <tr>
            <td>L. Sensitive Personal Information</td>
            <td></td>
            <td>NO</td>
          </tr>
        </tbody>
      </table>
      <p>
        We may also collect other personal information outside of these
        categories instances where you interact with us in person, online, or by
        phone or mail in the context of:
      </p>
      <ul className="list">
        <li>Receiving help through our customer support channels;</li>
        <li>Participation in customer surveys or contests; and</li>
        <li>
          Facilitation in the delivery of our Services and to respond to your
          inquiries.
        </li>
      </ul>
      <h4>How do we use and share your personal information?</h4>
      <p>
        More information about our data collection and sharing practices can be
        found in this privacy notice.
      </p>
      <p>
        You may contact us by email at{" "}
        <a href="mailto:techinvento@icloud.com">
          &nbsp;techinvento@icloud.com
        </a>
        ,
        <a href="mailto:devtechinventoinfo@gmail.com">
          &nbsp;devtechinventoinfo@gmail.com
        </a>
        , or by referring to the contact details at the bottom of this document.
      </p>
      <p>
        If you are using an authorized agent to exercise your right to opt out
        we may deny a request if the authorized agent does not submit proof that
        they have been validly authorized to act on your behalf.
      </p>
      <h4>Will your information be shared with anyone else?</h4>
      <p>
        We may disclose your personal information with our service providers
        pursuant to a written contract between us and each service provider.
        Each service provider is a for-profit entity that processes the
        information on our behalf, following the same strict privacy protection
        obligations mandated by the CCPA.
      </p>
      <p>
        We may use your personal information for our own business purposes, such
        as for undertaking internal research for technological development and
        demonstration. This is not considered to be “selling” of your personal
        information.
      </p>
      <p>
        Devtechinvento has not disclosed or sold any personal information to
        third parties for a business or commercial purpose in the preceding 12
        months. Devtechinvento will not sell personal information in the future
        belonging to website visitors, users, and other consumers.
      </p>
      <h4>Your rights with respect to your personal data</h4>
      <p>
        <u>Right to request deletion of the data - Request to delete</u>
      </p>
      <p>
        You can ask for the deletion of your personal information. If you ask us
        to delete your personal information, we will respect your request and
        delete your personal information, subject to certain exceptions provided
        by law, such as (but not limited to) the exercise by another consumer of
        his or her right to free speech, our compliance requirements resulting
        from a legal obligation, or any processing that may be required to
        protect against illegal activities.
      </p>
      <p>
        <u>Right to be informed - Request to know</u>
      </p>
      <p>Depending on the circumstances, you have a right to know:</p>
      <ul className="list">
        <li>whether we collect and use your personal information;</li>
        <li>the categories of personal information that we collect;</li>
        <li>
          the purposes for which the collected personal information is used;
        </li>
        <li>whether we sell your personal information to third parties;</li>
        <li>
          the categories of personal information that we sold or disclosed for a
          business purpose;
        </li>
        <li>
          the categories of third parties to whom the personal information was
          sold or disclosed for a business purpose; and
        </li>
        <li>
          the business or commercial purpose for collecting, selling, or
          disclosing the personal information.
        </li>
      </ul>
      <p>
        In accordance with applicable law, we are not obligated to provide or
        delete consumer information that is de-identified in response to a
        consumer request or to re-identify individual data to verify a consumer
        request.
      </p>
      <p>
        <u>
          Right to Non-Discrimination for the Exercise of a Consumer’s Privacy
          Rights
        </u>
      </p>
      <p>
        We will not discriminate against you if you exercise your privacy
        rights.
      </p>

      <p>
        <u>
          Right to Limit Use and Disclosure of Sensitive Personal Information
        </u>
      </p>
      <p>We do not process consumer’s sensitive personal information.</p>

      <p>
        <u>Verification process</u>
      </p>
      <p>
        Upon receiving your request, we will need to verify your identity to
        determine whether you are the same person with whom we have the
        information in our system. These verification efforts require us to ask
        you to provide information so that we can match it with the information
        you have previously provided us. For instance, depending on the type of
        request you submit, we may ask you to provide certain information so
        that we can match the information you provide with the information we
        already have on file, or we may contact you through a communication
        method (e.g., phone or email) that you have previously provided to us.
        We may also use other verification methods as the circumstances dictate.
      </p>
      <p>
        We will only use personal information provided in your request to verify
        your identity or authority to make the request. To the extent possible,
        we will avoid requesting additional information from you for the
        purposes of verification. However, if we cannot verify your identity
        from the information already maintained by us, we may request that you
        provide additional information for the purposes of verifying your
        identity and for security or fraud-prevention purposes. We will delete
        such additionally provided information as soon as we finish verifying
        you.
      </p>
      <p>
        <u>Other privacy rights</u>
      </p>
      <ul className="list">
        <li>you may object to the processing of your personal information</li>
        <li>
          you may request correction of your personal data if it is incorrect or
          no longer relevant, or ask to restrict the processing of the
          information
        </li>
        <li>
          you can designate an authorized agent to make a request under the CCPA
          on your behalf. We may deny a request from an authorized agent that
          does not submit proof that they have been validly authorized to act on
          your behalf in accordance with the CCPA.
        </li>
        <li>
          You may request to opt out from future selling of your personal
          information to third parties. Upon receiving a request to opt out, we
          will act upon the request as soon as feasibly possible, but no later
          than fifteen (15) days from the date of the request submission.
        </li>
      </ul>
      <p>
        To exercise these rights, you can contact us by email at{" "}
        <a href="mailto:techinvento@icloud.com">
          &nbsp;techinvento@icloud.com
        </a>
        ,{" "}
        <a href="mailto:devtechinventoinfo@gmail.com">
          &nbsp;devtechinventoinfo@gmail.com
        </a>
        , or by referring to the contact details at the bottom of this document.
        If you have a complaint about how we handle your data, we would like to
        hear from you.
      </p>
      <h3 ref={topicTenRef}>10. DO WE MAKE UPDATES TO THIS NOTICE?</h3>
      <p>
        <i>
          <b>In Short:</b> Yes, will update this notice as necessary to stay
          compliant with relevant laws.
        </i>
      </p>
      <p>
        We may update this privacy notice from time to time. The updated version
        will be indicated by an updated “Revised” date and the updated version
        will be effective as soon as it is accessible. If we make material
        changes to this privacy notice, we may notify you either by prominently
        posting a notice of such changes or by directly sending you a
        notification. We encourage you to review this privacy notice frequently
        to be informed of how we are protecting your information.
      </p>
      <h3 ref={topicElevenRef}>
        11. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
      </h3>
      <p>
        If you have questions or comments about this notice, you may email us at{" "}
        <a href="mailto:techinvento@icloud.com">
          &nbsp;techinvento@icloud.com
        </a>
        ,{" "}
        <a href="mailto:devtechinventoinfo@gmail.com">
          &nbsp;devtechinventoinfo@gmail.com
        </a>{" "}
        or by post to:
      </p>
      <p>
        Devtechinvento
        <br />
        4206 Pyke Dr
        <br />
        Powell, OH 43065
        <br />
        United States
        <br />
      </p>
      <h3 ref={topicTwelveRef}>
        12. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
      </h3>
      <p>
        You have the right to request access to the personal information we
        collect from you, change that information, or delete it. To request to
        review, update, or delete your personal information, please fill out and
        submit a
        <a
          href="https://app.termly.io/notify/2d1dd86f-7215-4226-915d-026540d4d0bb"
          target="_blank"
          rel="noreferrer"
        >
          &nbsp;data subject access request.
        </a>
      </p>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
